import { MessageDescriptor } from 'react-intl';

import { efficacyValueProps, featuresValueProps, goalBasedContentValueProps } from './data';
import messages from './messages';
import { Item, Section } from './Retention/types';
import { CancellationFlowScreen, CancellationReason, RetentionScreenType } from './types';

export const SPECIAL_OFFER_COUPON = '40percent_retention_offer';
export const SPECIAL_OFFER_DISCOUNT = '40%';
export const CANCELLATION_FLOW_EXPERIMENT = 'www-cancellation-flow';

export const retentionScreenContentMap: Record<
	RetentionScreenType,
	{ title: MessageDescriptor; description: MessageDescriptor; valueProps: Item[] | Record<string, Section> }
> = {
	[RetentionScreenType.FEATURES]: {
		title: messages.featuresTitle,
		description: messages.featuresDescription,
		valueProps: featuresValueProps,
	},
	[RetentionScreenType.EFFICACY]: {
		title: messages.efficacyTitle,
		description: messages.efficacyDescription,
		valueProps: efficacyValueProps,
	},
	[RetentionScreenType.GOAL_BASED_CONTENT]: {
		title: messages.goalBasedContentTitle,
		description: messages.goalBasedContentDescription,
		valueProps: goalBasedContentValueProps,
	},
};

const cancellationReasonMap: Record<RetentionScreenType, CancellationReason[]> = {
	[RetentionScreenType.EFFICACY]: [
		CancellationReason.INFREQUENT_USAGE,
		CancellationReason.NO_PERSONAL_PROGRESS,
	],
	[RetentionScreenType.FEATURES]: [
		CancellationReason.DIFFICULT_CONTENT_ACCESS,
		CancellationReason.NO_LONGER_NEEDED,
	],
	[RetentionScreenType.GOAL_BASED_CONTENT]: [
		CancellationReason.IRRELEVANT_CONTENT,
		CancellationReason.INADEQUATE_CONTENT,
	],
};

// Accepts string[] (the shared context reason field is widened to string[]).
// Only legacy CancellationReason values ever match a retention bucket.
export const getRetentionScreenTypeByCancellationReasons = (
	cancellationReasons: string[],
): RetentionScreenType => {
	const retentionScreenPriority: Record<RetentionScreenType, number> = {
		[RetentionScreenType.EFFICACY]: 1,
		[RetentionScreenType.FEATURES]: 2,
		[RetentionScreenType.GOAL_BASED_CONTENT]: 3,
	};

	const sortedRetentionTypes = Object.keys(retentionScreenPriority).sort(
		(a, b) =>
			retentionScreenPriority[a as RetentionScreenType] - retentionScreenPriority[b as RetentionScreenType],
	) as RetentionScreenType[];

	for (const retentionType of sortedRetentionTypes) {
		const reasonsForType: string[] = cancellationReasonMap[retentionType];
		if (cancellationReasons.some(reason => reasonsForType.includes(reason))) {
			return retentionType;
		}
	}

	return RetentionScreenType.FEATURES;
};

export const analyticsPageNameMap: Record<
	Exclude<CancellationFlowScreen, CancellationFlowScreen.RETENTION> | RetentionScreenType,
	string
> = {
	[CancellationFlowScreen.QUESTIONNAIRE]: 'cancellation survey',
	[RetentionScreenType.FEATURES]: 'features education',
	[RetentionScreenType.EFFICACY]: 'efficacy education',
	[RetentionScreenType.GOAL_BASED_CONTENT]: 'goal-based education',
	[CancellationFlowScreen.OFFER]: 'cancellation special offer',
	[CancellationFlowScreen.OFFER_SUCCESS]: 'cancellation special offer confirmation',
	[CancellationFlowScreen.CANCEL_SUBSCRIPTION]: 'final cancellation confirmation',
	[CancellationFlowScreen.CANCELLATION_SUCCESS]: 'sub cancellation confirmation',
	[CancellationFlowScreen.ERROR]: 'sub cancellation error',
};
